import React, {useState, useMemo, useEffect} from 'react';
import {
  Box,
  Typography,
  Paper,
  Grid,
  IconButton, useTheme, Select, MenuItem, InputLabel, FormControl,
} from '@mui/material';
import VideoDetail from "../components/System/VideoDetail";
import HelpOutlineIcon from "@mui/icons-material/HelpOutline";
import HelpModal from "../components/Common/HelpModal";
import {getMyMenuRoles} from "../api/menuRoleApi";
import useLocalStorageVO from "../components/Common/UseLocalStorageVO";


const VideoManual = () => {
  const theme = useTheme();
  const isDarkMode = theme.palette.mode === 'dark';
  const [isHelpModalOpen, setIsHelpModalOpen] = useState(false);

  // 상태
  const [menuItems, setMenuItems] = useState([
    {
      id: 'ci',
      name: '기준정보관리',
      icon: 'ℹ️',
      isActive: false,
      subItems: [
        { id: 'ci-common', name: '공통코드관리' },
        { id: 'ci-factory', name: '공장정보관리' },
        { id: 'ci-line', name: '라인정보관리' },
        { id: 'ci-equipment', name: '설비정보관리' },
        { id: 'ci-customer', name: '거래처관리' },
        { id: 'ci-warehouse', name: '창고정보관리' }
      ]
    },
    {
      id: 'pi',
      name: '품목정보관리',
      icon: '📋',
      isActive: false,
      subItems: [
        { id: 'pi-material-management', name: '원/부자재관리' },
        { id: 'pi-half-product', name: '반제품관리' },
        { id: 'pi-product', name: '제품관리' },
        { id: 'pi-bom', name: 'BOM관리' }
      ]
    },
    {
      id: 'sm',
      name: '영업관리',
      icon: '💼',
      isActive: false,
      subItems: [
        { id: 'sm-order', name: '주문등록' },
        { id: 'sm-sales', name: '출하관리' },
        { id: 'sm-transaction', name: '거래명세서관리' }
      ]
    },
    {
      id: 'mi',
      name: '자재/재고관리',
      icon: '📦',
      isActive: false,
      subItems: [
        { id: 'mi-inbound', name: '입고관리' },
        { id: 'mi-outbound', name: '출고관리' },
        { id: 'mi-stock', name: '자재/재고현황 + 재고상세이력' },
      ]
    },
    {
      id: 'pm',
      name: '생산관리',
      icon: '🏭',
      isActive: false,
      subItems: [
        { id: 'mm-plan', name: '생산계획관리' },
        { id: 'mm-workorder', name: '작업지시관리' },
        { id: 'mm-result-in', name: '생산실적등록' },
        { id: 'mm-kpi-result-in', name: 'KPI생산실적등록' },
        { id: 'mm-result', name: '생산실적조회' },
        { id: 'mm-defect', name: '불량조회' }
      ]
    },
    {
      id: 'mo',
      name: '모니터링',
      icon: '📊',
      isActive: false,
      subItems: [
        { id: 'mo-kpi', name: 'KPI모니터링' }
      ]
    },
    {
      id: 'rp',
      name: '리포트',
      icon: '📝',
      isActive: false,
      subItems: [
        { id: 'rp-mpv', name: '계획대비실적조회' },
        { id: 'rp-ppr', name: '기간별생산실적' },
        { id: 'rp-dpr', name: '불량률 현황' },
        { id: 'rp-imr', name: '입출고현황' }
      ]
    },
    {
      id: 'sy',
      name: '시스템',
      icon: '⚙️',
      isActive: false,
      subItems: [
        { id: 'sy-notice', name: '공지사항' },
        { id: 'sy-authority', name: '권한관리' },
        { id: 'sy-user', name: '사용자관리' },
        { id: 'sy-company', name: '회사정보' },
      ]
    }
  ]);
  const [selectedMainMenuId, setSelectedMainMenuId] = useState('');
  const [selectedMenuId, setSelectedMenuId] = useState('');
  const { loginUser } = useLocalStorageVO();

  // 메뉴 권한(오픈 상태)에 따라 menuItems를 필터링
  useEffect(() => {
    async function fetchAndFilterMenus() {
      try {
        const rawRoles = await getMyMenuRoles();
        const roleList = rawRoles?.getMyMenuRoles || [];
        const openMenuIds = new Set(
            roleList
                .filter(role => role.isOpen === true)
                .map(role => (role.menuId || '').trim().toLowerCase())
        );

        const userLevel = loginUser.priorityLevel

        setMenuItems(prev => {
          const filtered = prev
              .map(group => {
                let visibleSubItems = group.subItems?.filter(
                    sub => openMenuIds.has((sub.id || '').trim().toLowerCase())
                ) || [];
                if (group.id === 'sy' && userLevel <= 2) {
                  visibleSubItems = [{ id: 'sy-default-user', name: '공지사항/회사정보' }];
                }
                const isGroupVisible = openMenuIds.has((group.id || '').trim().toLowerCase()) || visibleSubItems.length > 0;
                return isGroupVisible ? { ...group, subItems: visibleSubItems } : null;
              })
              .filter(Boolean);
          return filtered;
        });
      } catch (e) {
        console.error('메뉴 권한 조회 실패:', e);
      }
    }
    fetchAndFilterMenus();
  }, []);


  // 셀렉트박스 메뉴 선택
  const handleMainMenuChange = (event) => {
    const newMainMenuId = event.target.value;
    setSelectedMainMenuId(newMainMenuId);
    setSelectedMenuId('');
  };

  const handleSubMenuChange = (event) => {
    setSelectedMenuId(event.target.value);
  };

  const subMenuItems = useMemo(() => {
    if (!selectedMainMenuId) return [];
    const selectedMainItem = menuItems.find(item => item.id === selectedMainMenuId);
    return selectedMainItem ? selectedMainItem.subItems : [];
  }, [selectedMainMenuId, menuItems]);


  return (<>
    <Box sx={{ p: 2 }}>
      {/* --- 상단 헤더 및 셀렉트 박스 영역 --- */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <Typography variant="h5" component="h2" sx={{ fontWeight: 600, flexShrink: 0 }}>
            IMOS 영상 매뉴얼
          </Typography>

          {/* 상위 메뉴 셀렉트 박스 */}
          <FormControl sx={{ minWidth: 180 }} size="small">
            <InputLabel id="sub-menu-select-label">대분류</InputLabel>
            <Select
                label={'대분류'}
                labelId="main-menu-select-label"
                value={selectedMainMenuId}
                onChange={handleMainMenuChange}
                displayEmpty
            >
              {menuItems.map((item) => (
                  <MenuItem key={item.id} value={item.id}>
                    {item.name}
                  </MenuItem>
              ))}
            </Select>
          </FormControl>

          {/* 하위 메뉴 셀렉트 박스 */}
          <FormControl sx={{ minWidth: 180 }} size="small" disabled={!selectedMainMenuId}>
            <InputLabel id="sub-menu-select-label">소분류</InputLabel>
            <Select
                label={'소분류'}
                labelId="sub-menu-select-label"
                value={selectedMenuId}
                onChange={handleSubMenuChange}
                displayEmpty
            >
              {subMenuItems.map((subItem) => (
                  <MenuItem key={subItem.id} value={subItem.id}>
                    {subItem.name}
                  </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Box>

        <IconButton
            onClick={() => setIsHelpModalOpen(true)}
            sx={{
              color: isDarkMode ? theme.palette.primary.light : theme.palette.primary.main,
            }}
        >
          <HelpOutlineIcon />
        </IconButton>
      </Box>

      {/* --- 하단 비디오 상세 영역 --- */}
      <Grid container>
        <Grid item xs={12}>
          <Paper sx={{ p: 2, minHeight: '75vh', display: 'flex', flexDirection: 'column' }}>
            {selectedMenuId ? (
                <VideoDetail menuId={selectedMenuId} />
            ) : (
                <Box sx={{
                  display: 'flex',
                  justifyContent: 'center',
                  alignItems: 'center',
                  width: '100%',
                  minHeight: '80vh'
                }}>
                  <Typography color="text.secondary">시청하실 메뉴를 선택해주세요.</Typography>
                </Box>

            )}
          </Paper>
        </Grid>
      </Grid>
    </Box>
    <HelpModal
        open={isHelpModalOpen}
        onClose={() => setIsHelpModalOpen(false)}
        title="IMOS 영상 매뉴얼 도움말"
    >
      <Typography variant="body2">
        IMOS 시스템 사용법을 동영상으로 상세하게 안내합니다.<br/>
        선택박스에서 원하는 메뉴를 선택하면 매뉴얼을 볼 수 있습니다.
      </Typography>
    </HelpModal>
  </>);
};

export default VideoManual;